/* LAV TDMS — «Мои действия»: personal action inbox (ASSAI-style outstanding
   items). The owner's home screen — a single action-centric queue of every
   document-control task awaiting the user, grouped by action type, with SLA
   countdowns and one-click routing into the relevant surface. window.APP.Inbox */
import { useState, type ReactNode } from 'react'
import { useSub } from '@/data/queries'
import { useDictLabels, useMe } from '@/data/meta'
import { parseRu } from '@/lib/format'
import type { Purpose as PurposeCode, ReviewCode } from '@/data/types'
import {
  Button,
  Avatar,
  PageHead,
  Purpose,
  CodeChip,
  DiscTag,
  Segmented,
  SlaPill,
  Spinner,
  EmptyState, clickable } from '@/ui'
import { Icon } from '@/ds/icons'
import { t, type Lang } from '@/i18n/lang'



// action-type catalogue (the columns of the owner workflow)
/** Вид задачи в очереди: подписи, иконка, цвета и надпись на кнопке действия. */
interface KindStyle {
  ru: string
  en: string
  icon: string
  color: string
  bg: string
  line: string
  cta: { ru: string; en: string }
}

const KIND: Record<string, KindStyle | undefined> = {
  check:   { ru: "Входной контроль", en: "Intake check", icon: "scan-search", color: "--blue-500", bg: "--blue-50", line: "--blue-100", cta: { ru: "Принять", en: "Accept" } },
  review:  { ru: "Рассмотрение", en: "Review", icon: "eye", color: "--amber-500", bg: "--amber-50", line: "--amber-100", cta: { ru: "Рассмотреть", en: "Review" } },
  approve: { ru: "Консолидация и утверждение", en: "Consolidate & approve", icon: "layers", color: "--violet-500", bg: "--violet-50", line: "--violet-100", cta: { ru: "Консолидировать", en: "Consolidate" } },
  translate: { ru: "Перевод замечаний", en: "Translate findings", icon: "type", color: "--blue-600", bg: "--blue-50", line: "--blue-100", cta: { ru: "Перевести", en: "Translate" } },
  issue:   { ru: "Выпуск возвратного TRM", en: "Issue return TRM", icon: "send", color: "--amber-600", bg: "--amber-50", line: "--amber-100", cta: { ru: "Выпустить TRM", en: "Issue TRM" } },
  acsr:    { ru: "Решение по ответу ACSR", en: "Disposition (ACSR)", icon: "message-square-reply", color: "--green-600", bg: "--green-50", line: "--green-100", cta: { ru: "Решение", en: "Disposition" } },
}
const ORDER = ["check", "review", "translate", "approve", "issue", "acsr"]

/** Позиция очереди «Мои задачи»: пришедший трансмиттал либо ответ на замечание.
 *  Форма совпадает с ответом GET /api/inbox — очередь собирает сервер. */
interface InboxItem {
  kind: string
  ref: string
  refKind: 'trm' | 'crs'
  title: string
  meta: {
    disc?: string | null
    docs?: number
    from?: string
    purpose?: PurposeCode | '—'
    doc?: string
    code?: ReviewCode | null
  }
  due: string
  owner: string
  nav: 'review' | 'crs'
}

const EMPTY: InboxItem[] = []

/* Замороженного «сегодня» из seed на этом экране больше нет: очередь приходит
   с сервера, и просрочка обязана считаться от настоящей даты. */
function daysLeft(due: string): number | null {
  const d = parseRu(due)
  if (!d) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((d.getTime() - today.getTime()) / 86_400_000)
}

function overdueCount(items: InboxItem[]): number { return items.filter((i) => { const d = daysLeft(i.due); return d != null && d < 0 }).length }
function todayCount(items: InboxItem[]): number { return items.filter((i) => { const d = daysLeft(i.due); return d != null && d >= 0 && d <= 2 }).length }

function ActionCard({ it, lang, onOpen, onOpenObj }: { it: InboxItem; lang: Lang; onOpen: () => void; onOpenObj?: () => void }) {
  const k = KIND[it.kind]
  return (
    <div className="tdms-actcard tdms-rail" style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-md)", cursor: "pointer" }} {...clickable(onOpen)}>
      <span style={{ display: "grid", placeItems: "center", width: 34, height: 34, flex: "0 0 auto", borderRadius: "var(--radius-sm)", background: `var(${k?.bg})`, color: `var(${k?.color})`, border: `1px solid var(${k?.line})` }}><Icon name={k?.icon ?? "circle"} size={17} /></span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span {...clickable((e) => { e.stopPropagation(); onOpenObj?.() })} className="tdms-reflink" style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, fontWeight: 600, color: "var(--text-link)" }}>{it.ref}</span>
          <DiscTag code={it.meta.disc ?? ''} />
          {it.meta.purpose ? <Purpose value={it.meta.purpose} size="sm" /> : null}
          {it.meta.code ? <CodeChip code={it.meta.code} size="sm" /> : null}
        </div>
        <div style={{ font: "var(--font-ui)", fontSize: "var(--text-sm)", color: "var(--text-body)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 520 }}>{it.title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 5, font: "var(--font-caption)", color: "var(--text-subtle)" }}>
          {it.meta.docs != null ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="files" size={12} />{it.meta.docs} док.</span> : null}
          {it.meta.doc ? <span style={{ fontFamily: "var(--font-mono)" }}>{it.meta.doc}</span> : null}
          {it.meta.from ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="building-2" size={12} />{it.meta.from}</span> : null}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Avatar name={it.owner} size={15} />{it.owner}</span>
        </div>
      </div>
      <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 7 }}>
        <SlaPill due={it.due} />
        <Button variant="primary" size="sm" iconRight={<Icon name="arrow-right" size={13} />} onClick={(e) => { e.stopPropagation(); onOpen() }}>{t(lang, k?.cta?.ru ?? "", k?.cta?.en ?? "")}</Button>
      </div>
    </div>
  )
}

export function Inbox({ lang = 'ru', onNav, onOpenTrm }: { lang?: Lang; onNav: (v: string) => void; onOpenDoc?: (ref: string) => void; onOpenViewer?: (ref: string) => void; onOpenTrm?: (ref: string) => void }) {
  const queue = useSub<InboxItem[]>("/inbox")
  const me = useMe().data
  const discLabels = useDictLabels("disc")
  const [mode, setMode] = useState<'kind' | 'due' | 'disc'>("kind") // kind | due | disc
  const [filter, setFilter] = useState<'all' | 'overdue' | 'soon'>("all") // all | overdue | soon
  // Ответ сервера — вся очередь целиком, поэтому считать по нему можно прямо здесь.
  const all = queue.data ?? EMPTY

  const items = all.filter((i) => {
    if (filter === "all") return true
    const d = daysLeft(i.due)
    if (filter === "overdue") return d != null && d < 0
    if (filter === "soon") return d != null && d >= 0 && d <= 2
    return true
  })
  /* Пока очередь не пришла, плитки показывают «—»: ноль читался бы как «задач нет». */
  const tile = (n: number): ReactNode => (queue.isSuccess ? n : "—")

  /* Переход на разбор конкретной записи — это адрес, а не отметка на window:
     /review/TRM-0042 открывается по ссылке и переживает возврат назад. */
  const open = (it: InboxItem) => {
    if (it.nav === "review" || it.nav === "crs") onNav(`${it.nav}/${it.ref}`)
  }
  const openObj = (it: InboxItem) => { if (it.refKind === "trm") onOpenTrm?.(it.ref) }

  // grouping
  let groups: { key: string; label: string; icon: string; color: string; items: InboxItem[] }[] = []
  if (mode === "kind") {
    groups = ORDER.map((k) => ({ key: k, label: t(lang, KIND[k]?.ru ?? k, KIND[k]?.en ?? k), icon: KIND[k]?.icon ?? "circle", color: KIND[k]?.color ?? "--text-muted", items: items.filter((i) => i.kind === k) })).filter((g) => g.items.length)
  } else if (mode === "due") {
    const buckets = [
      { key: "od", label: t(lang, "Просрочено", "Overdue"), icon: "flame", color: "--danger-ink", test: (d: number | null) => d != null && d < 0 },
      { key: "now", label: t(lang, "Сегодня / 2 дня", "Today / 2 days"), icon: "alarm-clock", color: "--warning-ink", test: (d: number | null) => d != null && d >= 0 && d <= 2 },
      { key: "wk", label: t(lang, "На этой неделе", "This week"), icon: "calendar", color: "--blue-600", test: (d: number | null) => d != null && d > 2 && d <= 7 },
      { key: "later", label: t(lang, "Позже / без срока", "Later / no due"), icon: "clock", color: "--text-subtle", test: (d: number | null) => d == null || d > 7 },
    ]
    groups = buckets.map((b) => ({ key: b.key, label: b.label, icon: b.icon, color: b.color, items: items.filter((i) => b.test(daysLeft(i.due))) })).filter((g) => g.items.length)
  } else {
    const discs = [...new Set(items.map((i) => i.meta.disc ?? ""))]
    groups = discs.map((dc) => ({ key: dc, label: dc + " — " + (discLabels[dc] || dc), icon: "folder-tree", color: "--text-muted", items: items.filter((i) => (i.meta.disc ?? "") === dc) }))
  }

  return (
    <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", background: "var(--surface-page)" }}>
      <PageHead
        crumbs={<span>{me?.project ?? ""} <span style={{ color: "var(--border-strong)" }}>/</span> {t(lang, "Мои действия", "My actions")}</span>}
        title={t(lang, "Мои действия", "My actions")}
        sub={t(lang, "Персональная очередь — то, что ждёт вашего решения", "Your personal queue of outstanding items")}
        actions={<Segmented size="sm" value={mode} onChange={setMode} options={[
          { value: "kind", label: t(lang, "По действию", "By action"), icon: "list-checks" },
          { value: "due", label: t(lang, "По сроку", "By due"), icon: "alarm-clock" },
          { value: "disc", label: t(lang, "По дисциплине", "By discipline"), icon: "folder-tree" },
        ]} />}
      />

      {/* Summary strip */}
      <div style={{ display: "flex", gap: 10, padding: "16px 24px 4px" }}>
        <SummaryTile active={filter === "all"} onClick={() => setFilter("all")} icon="inbox" label={t(lang, "Всего задач", "Total tasks")} value={tile(all.length)} accent />
        <SummaryTile active={filter === "overdue"} onClick={() => setFilter("overdue")} icon="flame" label={t(lang, "Просрочено", "Overdue")} value={tile(overdueCount(all))} tone="danger" />
        <SummaryTile active={filter === "soon"} onClick={() => setFilter("soon")} icon="alarm-clock" label={t(lang, "Срок ≤ 2 дней", "Due ≤ 2 days")} value={tile(todayCount(all))} tone="warning" />
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 14px", background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-md)" }}>
          <Avatar name={me?.user.name ?? ""} size={26} />
          <div style={{ minWidth: 0 }}>
            <div style={{ font: "var(--font-ui)", fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-strong)" }}>{me?.user.name ?? ""}</div>
            <div style={{ font: "var(--font-caption)", color: "var(--text-subtle)" }}>{[me?.user.role, me?.user.disc?.join(", ")].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "12px 24px 32px" }}>
        {queue.isPending ? <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 8, font: "var(--font-ui)", color: "var(--text-subtle)" }}><Spinner size={13} />{t(lang, "Загрузка…", "Loading…")}</div> : null}
        {queue.isError ? <div style={{ padding: 14, font: "var(--font-ui)", color: "var(--danger-ink)" }}>{t(lang, "Не удалось загрузить очередь", "Failed to load the queue")}</div> : null}
        {!queue.isSuccess ? null : groups.length ? groups.map((g) => (
          <section key={g.key} style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
              <span style={{ display: "inline-flex", color: `var(${g.color})` }}><Icon name={g.icon} size={16} /></span>
              <span style={{ font: "var(--font-card-title)", color: "var(--text-strong)" }}>{g.label}</span>
              <span style={{ font: "var(--font-mono)", fontSize: 11, color: "var(--text-subtle)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-sm)", padding: "1px 7px" }}>{g.items.length}</span>
              <span style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {g.items.map((it: InboxItem, i: number) => <ActionCard key={it.ref + i} it={it} lang={lang} onOpen={() => open(it)} onOpenObj={() => openObj(it)} />)}
            </div>
          </section>
        )) : (
          <EmptyState icon="check-check" title={t(lang, "Очередь пуста", "All clear")} text={t(lang, "Нет задач по выбранному фильтру. Все действия выполнены.", "No tasks match the filter — you're all caught up.")} />
        )}
      </div>
      {/* Наведение только там, где есть указатель: на касании подсветка
          «липла» после того, как палец убрали. Hover карточки действия
          (.tdms-actcard) живёт в ui.css — та же карточка есть в очереди
          подрядчика, и локальное правило красило бы только этот экран. */}
      {/* У плитки-сводки hover только фоном: перекраска рамки стёрла бы
          акцентный borderTop у «Всего задач»; активная плитка не мигает.
          Номер объекта подчёркивается как настоящая ссылка — span'у правило
          base.css для <a> не достаётся. */}
      <style>{`@media (hover: hover) and (pointer: fine) { .tdms-sumtile:not([data-active]):hover { background: var(--surface-hover) !important; } .tdms-reflink:hover { text-decoration: underline; } }`}</style>
    </div>
  )
}

function SummaryTile({ icon, label, value, tone, accent, active, onClick }: { icon: string; label: ReactNode; value: ReactNode; tone?: 'danger' | 'warning'; accent?: boolean; active?: boolean; onClick?: () => void }) {
  const ink = tone === "danger" ? "--danger-ink" : tone === "warning" ? "--warning-ink" : "--text-strong"
  return (
    <button onClick={onClick} className="tool-btn tdms-sumtile" data-active={active || undefined}
            /* transform в transition обязан остаться: инлайновый transition перебил бы кривую нажатия tool-btn. */
            style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 168, padding: "12px 16px", background: active ? "var(--accent-soft)" : "var(--surface-card)", border: "1px solid " + (active ? "var(--accent)" : "var(--border-default)"), borderTop: accent && !active ? "2px solid var(--accent)" : undefined, borderRadius: "var(--radius-md)", cursor: "pointer", textAlign: "left", transition: "transform var(--duration-press) var(--ease-out), background var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard)" }}>
      <span style={{ display: "inline-flex", color: tone ? `var(${ink})` : "var(--text-subtle)" }}><Icon name={icon} size={18} /></span>
      <div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 22, fontWeight: 600, color: `var(${ink})`, lineHeight: 1 }}>{value}</div>
        <div style={{ font: "var(--font-eyebrow)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--text-subtle)", marginTop: 4 }}>{label}</div>
      </div>
    </button>
  )
}

